// src/utils/permissions.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getCurrentUser, ROLES } from "./auth";
import { PP_USER_READY_EVENT } from "./userEvents";
import {
  DEFAULT_LIMITED_CONFIG,
  DEFAULT_MATRIX,
  PAGE_DEFINITIONS,
  PERMISSION_LEVELS,
} from "../config/permissions";

const BASE_ROLES = ROLES.slice();

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const READ_ENDPOINT = "/role-permissions";
const ADMIN_SAVE_ENDPOINT = "/admin/role-permissions";
const MATRIX_STORAGE_KEY = "pp_role_matrix";
const LIMITED_STORAGE_KEY = "pp_role_matrix_limited";
const USER_STORAGE_KEY = "pp_user";
const HTML_ERROR_PREFIX = /^<!doctype/i;
const DEFAULT_FALLBACK_ROLE = "Overview";
const MAX_USER_ATTEMPTS = 15;
const USER_POLL_INTERVAL_MS = 100;
let permissionsFetchPromise = null;

function parseJsonSafely(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function extractMessageFromBody(status, text) {
  const trimmed = (text || "").trim();
  if (!trimmed) return "";
  if (HTML_ERROR_PREFIX.test(trimmed) || trimmed.toLowerCase().startsWith("<html")) {
    return "";
  }
  const json = parseJsonSafely(trimmed);
  if (json && typeof json === "object") {
    return json.error || json.message || "";
  }
  return trimmed;
}

function buildHttpError(resp, text) {
  const status = resp?.status ?? 0;
  let message = extractMessageFromBody(status, text);
  if (!message) {
    if (status === 404) {
      message = "角色權限 API 尚未啟用（HTTP 404）。";
    } else if (status === 403) {
      message = "HTTP 403";
    } else if (status >= 400) {
      message = `HTTP ${status}`;
    } else {
      message = "未知錯誤";
    }
  }
  const error = new Error(message);
  if (status) error.status = status;
  return error;
}

function formatProviderError(err) {
  if (!err) return "無法取得角色權限設定。";
  const status = err.status ?? err?.response?.status ?? err?.code;
  if (status === 404) {
    return "角色權限 API 尚未啟用（HTTP 404）。已使用預設設定。";
  }
  const message = (err.message || String(err) || "").trim();
  if (!message || HTML_ERROR_PREFIX.test(message)) {
    return "無法取得角色權限設定。";
  }
  return message;
}


function canonicalRoleName(role) {
  if (!role) return "";
  const normalized = String(role).trim();
  const match = ROLES.find((r) => r.toLowerCase() === normalized.toLowerCase());
  return match || normalized;
}

function normalizeRoleName(role) {
  if (!role && role !== "") return "";
  const normalized = canonicalRoleName(role);
  return String(normalized || "").trim();
}

function lookupRoleEntry(source = {}, role) {
  if (!source || !role) return {};
  if (source[role]) return source[role];
  const lower = String(role).toLowerCase();
  const key = Object.keys(source).find((name) => String(name).toLowerCase() === lower);
  return key ? source[key] || {} : {};
}

function collectRoles(matrix = {}, limited = {}) {
  const ordered = [];
  const seen = new Set();

  const push = (role) => {
    const normalized = normalizeRoleName(role);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    ordered.push(normalized);
  };

  BASE_ROLES.forEach(push);
  Object.keys(matrix || {}).forEach(push);
  Object.keys(limited || {}).forEach(push);

  return ordered;
}

function deepClone(value) {
  try {
    return JSON.parse(JSON.stringify(value ?? {}));
  } catch {
    return value ?? {};
  }
}

function buildFallbackMatrixEntry(role) {
  const canonical = normalizeRoleName(role) || DEFAULT_FALLBACK_ROLE;
  const templateRole = DEFAULT_MATRIX[canonical] ? canonical : DEFAULT_FALLBACK_ROLE;
  const template = DEFAULT_MATRIX[templateRole] || DEFAULT_MATRIX[DEFAULT_FALLBACK_ROLE] || {};
  const fallback = {};
  PAGE_DEFINITIONS.forEach(({ id }) => {
    if (Object.prototype.hasOwnProperty.call(template, id)) {
      fallback[id] = template[id];
    } else if (DEFAULT_MATRIX[DEFAULT_FALLBACK_ROLE] && Object.prototype.hasOwnProperty.call(DEFAULT_MATRIX[DEFAULT_FALLBACK_ROLE], id)) {
      fallback[id] = DEFAULT_MATRIX[DEFAULT_FALLBACK_ROLE][id];
    } else {
      fallback[id] = "None";
    }
  });
  return fallback;
}

function ensureRoleFallback(matrix, limited, role, reasonTag = "fallback") {
  const canonical = normalizeRoleName(role);
  if (!canonical) {
    return { matrix, limited, changed: false };
  }
  if (lookupRoleEntry(matrix, canonical)) {
    return { matrix, limited, changed: false };
  }
  const fallbackEntry = buildFallbackMatrixEntry(canonical);
  const nextMatrix = { ...matrix, [canonical]: fallbackEntry };
  let nextLimited = limited;
  const templateLimited =
    (DEFAULT_LIMITED_CONFIG[canonical] && deepClone(DEFAULT_LIMITED_CONFIG[canonical])) ||
    (DEFAULT_LIMITED_CONFIG[DEFAULT_FALLBACK_ROLE] && deepClone(DEFAULT_LIMITED_CONFIG[DEFAULT_FALLBACK_ROLE]));
  if (templateLimited) {
    nextLimited = { ...limited, [canonical]: templateLimited };
  }
  console.warn(`permissions: injected fallback permissions for ${canonical} (${reasonTag})`);
  return { matrix: nextMatrix, limited: nextLimited, changed: true };
}

function buildStateFromPayload(rawMatrix, rawLimited, fallbackRole, reasonTag) {
  const sourceMatrix = rawMatrix && typeof rawMatrix === "object" ? rawMatrix : {};
  const sourceLimited = rawLimited && typeof rawLimited === "object" ? rawLimited : {};
  const roleHints = collectRoles(sourceMatrix, sourceLimited);
  const normalizedMatrix = normalizeMatrix(sourceMatrix, roleHints);
  const normalizedLimited = normalizeLimitedConfig(sourceLimited, roleHints);
  const seeded = ensureRoleFallback(normalizedMatrix, normalizedLimited, fallbackRole, reasonTag);
  const roles = collectRoles(seeded.matrix, seeded.limited);
  return {
    matrix: seeded.matrix,
    limited: seeded.limited,
    roles,
  };
}

function normalizeMatrix(raw = {}, roleList) {
  const roles = (roleList && roleList.length ? roleList : collectRoles(raw, {})).map((role) =>
    normalizeRoleName(role)
  );
  const seen = new Set();
  const result = {};
  roles.forEach((role) => {
    if (!role) return;
    const key = role.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);

    const incoming = lookupRoleEntry(raw, role);
    const defaults = DEFAULT_MATRIX[role] || {};
    const next = {};
    PAGE_DEFINITIONS.forEach(({ id }) => {
      const rawValue = incoming[id];
      const fallback = defaults[id] || "None";
      next[id] = PERMISSION_LEVELS.includes(rawValue) ? rawValue : fallback;
    });
    result[role] = next;
  });
  return result;
}

function normalizeLimitedConfig(raw = {}, roleList) {
  const roles = (roleList && roleList.length ? roleList : collectRoles({}, raw)).map((role) =>
    normalizeRoleName(role)
  );
  const seen = new Set();
  const result = {};
  roles.forEach((role) => {
    if (!role) return;
    const key = role.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);

    const defaults = DEFAULT_LIMITED_CONFIG[role] || {};
    const incoming = lookupRoleEntry(raw, role);
    const merged = { ...defaults };
    Object.keys(incoming || {}).forEach((entryKey) => {
      const defaultEntry = defaults[entryKey] || {};
      const incomingEntry = incoming[entryKey] || {};
      merged[entryKey] = {
        ...defaultEntry,
        ...incomingEntry,
        scope: {
          ...(defaultEntry.scope || { type: "all", field: null }),
          ...(incomingEntry.scope || {}),
        },
        features: {
          ...(defaultEntry.features || {}),
          ...(incomingEntry.features || {}),
        },
        fieldAccess: {
          ...(defaultEntry.fieldAccess || {}),
          ...(incomingEntry.fieldAccess || {}),
        },
      };
    });
    result[role] = merged;
  });
  return result;
}

function readStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.warn("permissions: failed to read storage", err);
    return null;
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.warn("permissions: failed to persist", err);
  }
}

function getDefaultState() {
  return buildStateFromPayload(DEFAULT_MATRIX, DEFAULT_LIMITED_CONFIG, DEFAULT_FALLBACK_ROLE, "default-state");
}

function getUserFromStorage() {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function resolveSignedInUser() {
  return getUserFromStorage() || getCurrentUser();
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForUser(maxAttempts = MAX_USER_ATTEMPTS, delayMs = USER_POLL_INTERVAL_MS) {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const user = resolveSignedInUser();
    if (user?.email) {
      return user;
    }
    await delay(delayMs);
  }
  return resolveSignedInUser();
}

function isAdminRole(role) {
  return String(role || "").trim().toLowerCase() === "admin";
}

function buildAuthHeaders(explicitUser) {
  const user = explicitUser || resolveSignedInUser();
  const headers = {};
  if (user?.email) headers["X-User-Email"] = user.email;
  if (user?.role) headers["X-User-Role"] = user.role;
  return { headers, user };
}

function buildPermissionsUrl(method = "GET") {
  const upper = String(method || "GET").toUpperCase();
  return upper === "GET" ? `${API_BASE}${READ_ENDPOINT}` : `${API_BASE}${ADMIN_SAVE_ENDPOINT}`;
}

async function fetchRemotePermissions() {
  if (permissionsFetchPromise) {
    return permissionsFetchPromise;
  }

  permissionsFetchPromise = (async () => {
    const resolvedUser = await waitForUser();
    if (!resolvedUser?.email) {
      throw new Error("missing_user_email");
    }
    const { headers, user } = buildAuthHeaders(resolvedUser);
    const resp = await fetch(buildPermissionsUrl("GET"), { headers });
    const text = await resp.text().catch(() => "");
    if (!resp.ok) {
      throw buildHttpError(resp, text);
    }
    const data = parseJsonSafely(text) || {};
    if (!data || typeof data !== "object") {
      throw new Error("Invalid response payload");
    }
    if (data?.ok === false) {
      throw new Error(data.error || data.message || "load_failed");
    }
    const scope = data.scope === "full" ? "remote-full" : "remote-limited";
    return buildStateFromPayload(
      data.matrix || {},
      data.limited || {},
      data.role || user?.role,
      scope
    );
  })();

  try {
    return await permissionsFetchPromise;
  } finally {
    permissionsFetchPromise = null;
  }
}

async function persistRemotePermissions(payload) {
  const resolvedUser = await waitForUser();
  if (!isAdminRole(resolvedUser?.role)) {
    throw new Error("forbidden");
  }
  const { headers, user } = buildAuthHeaders(resolvedUser);
  const reqHeaders = { ...headers, "Content-Type": "application/json" };
  const resp = await fetch(buildPermissionsUrl("PUT"), {
    method: "PUT",
    headers: reqHeaders,
    body: JSON.stringify(payload),
  });
  const text = await resp.text().catch(() => "");
  if (!resp.ok) {
    throw buildHttpError(resp, text);
  }
  const data = parseJsonSafely(text) || {};
  if (data?.ok === false) {
    throw new Error(data.error || data.message || "儲存失敗");
  }
  const rawMatrix = data.matrix || payload.matrix || {};
  const rawLimited = data.limited || payload.limited || {};
  return buildStateFromPayload(rawMatrix, rawLimited, user?.role, "persist");
}

const defaultState = getDefaultState();
const USER_READY_EVENT = PP_USER_READY_EVENT;
const REFRESH_RETRY_INTERVAL_MS = 300;
const REFRESH_MAX_WAIT_MS = 10000;

const PermissionsContext = createContext({
  loading: false,
  error: null,
  matrix: defaultState.matrix,
  limited: defaultState.limited,
  roles: defaultState.roles,
  refresh: async () => {},
  save: async () => {},
  getLevel: () => "None",
  getLimitedConfig: () => undefined,
});

export function PermissionsProvider({ children }) {
  const currentUser = resolveSignedInUser();
  const storedMatrix = readStorage(MATRIX_STORAGE_KEY);
  const storedLimited = readStorage(LIMITED_STORAGE_KEY);
  const initialState = buildStateFromPayload(
    storedMatrix || defaultState.matrix,
    storedLimited || defaultState.limited,
    currentUser?.role,
    storedMatrix || storedLimited ? "storage-init" : "default-init"
  );

  const [matrix, setMatrix] = useState(initialState.matrix);
  const [limited, setLimited] = useState(initialState.limited);
  const [roles, setRoles] = useState(initialState.roles);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const retryTimerRef = useRef(null);
  const retryStartRef = useRef(null);
  const missingUserWarnedRef = useRef(false);
  const refreshRef = useRef(null);

  const resetRetryState = useCallback(() => {
    retryStartRef.current = null;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const scheduleRetry = useCallback(() => {
    if (retryTimerRef.current) return;
    if (typeof window === "undefined") return;
    const now = Date.now();
    if (!retryStartRef.current) {
      retryStartRef.current = now;
    }
    if (now - retryStartRef.current > REFRESH_MAX_WAIT_MS) {
      return;
    }
    retryTimerRef.current = window.setTimeout(() => {
      retryTimerRef.current = null;
      if (typeof refreshRef.current === "function") {
        refreshRef.current();
      }
    }, REFRESH_RETRY_INTERVAL_MS);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRemotePermissions();
      resetRetryState();
      missingUserWarnedRef.current = false;
      setMatrix(data.matrix);
      setLimited(data.limited);
      setRoles(data.roles);
      writeStorage(MATRIX_STORAGE_KEY, data.matrix);
      writeStorage(LIMITED_STORAGE_KEY, data.limited);
    } catch (err) {
      const missingUser = err?.message === "missing_user_email";
      if (missingUser) {
        if (!missingUserWarnedRef.current) {
          console.warn("permissions: waiting for user profile before loading matrix");
          missingUserWarnedRef.current = true;
        }
        scheduleRetry();
      } else {
        console.warn("permissions: refresh failed", err);
        setError(formatProviderError(err));
        const cachedMatrix = readStorage(MATRIX_STORAGE_KEY);
        const cachedLimited = readStorage(LIMITED_STORAGE_KEY);
        const fallbackUser = getCurrentUser();
        const fallbackState = buildStateFromPayload(
          cachedMatrix || defaultState.matrix,
          cachedLimited || defaultState.limited,
          fallbackUser?.role,
          cachedMatrix || cachedLimited ? "cached-fallback" : "default-fallback"
        );
        if (cachedMatrix || cachedLimited) {
          console.warn("permissions: applied cached permissions after refresh failure");
        } else {
          console.warn("permissions: applied minimal default permissions after refresh failure");
        }
        setMatrix(fallbackState.matrix);
        setLimited(fallbackState.limited);
        setRoles(fallbackState.roles);
      }
    } finally {
      setLoading(false);
    }
  }, [scheduleRetry, resetRetryState]);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await refresh();
      } catch (err) {
        if (mounted) {
          console.warn("permissions: initial load failed", err);
        }
      }
    })();
    return () => {
      mounted = false;
    };
    // refresh has stable identity
  }, [refresh]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return () => {};
    }
    const handleReady = () => {
      if (!resolveSignedInUser()?.email) {
        resetRetryState();
        const signedOutState = getDefaultState();
        setMatrix(signedOutState.matrix);
        setLimited(signedOutState.limited);
        setRoles(signedOutState.roles);
        setLoading(false);
        setError(null);
        return;
      }
      refresh();
    };
    const handleStorage = (event) => {
      if (event.key === USER_STORAGE_KEY && event.newValue) {
        refresh();
      }
    };
    window.addEventListener(USER_READY_EVENT, handleReady);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(USER_READY_EVENT, handleReady);
      window.removeEventListener("storage", handleStorage);
    };
  }, [refresh, resetRetryState]);

  useEffect(() => () => {
    resetRetryState();
  }, [resetRetryState]);

  const save = useCallback(async (nextMatrix, nextLimited) => {
    setLoading(true);
    setError(null);
    try {
      const actor = resolveSignedInUser();
      if (!isAdminRole(actor?.role)) {
        throw new Error("forbidden");
      }
      const payload = {
        matrix: nextMatrix,
        limited: nextLimited,
      };
      const data = await persistRemotePermissions(payload);
      setMatrix(data.matrix);
      setLimited(data.limited);
      setRoles(data.roles);
      writeStorage(MATRIX_STORAGE_KEY, data.matrix);
      writeStorage(LIMITED_STORAGE_KEY, data.limited);
      return { ok: true };
    } catch (err) {
      setError(formatProviderError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getLevel = useCallback(
    (role, pageId) => {
      const canonical = canonicalRoleName(role);
      if (!canonical) return "None";
      const roleMap = matrix[canonical] || matrix[canonical.toLowerCase()];
      if (!roleMap) {
        const defaultsMap = defaultState.matrix[canonical] || {};
        return defaultsMap[pageId] || "None";
      }
      return roleMap[pageId] || "None";
    },
    [matrix]
  );

  const getLimitedConfig = useCallback(
    (role, key) => {
      const canonical = canonicalRoleName(role);
      const configs = limited[canonical] || limited[canonical.toLowerCase()];
      if (configs && key) return configs[key];
      return configs;
    },
    [limited]
  );

  const value = useMemo(
    () => ({
      loading,
      error,
      matrix,
      limited,
      roles,
      refresh,
      save,
      getLevel,
      getLimitedConfig,
    }),
    [loading, error, matrix, limited, roles, refresh, save, getLevel, getLimitedConfig]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  return useContext(PermissionsContext);
}

export function permissionLevelRank(level) {
  return PERMISSION_LEVELS.indexOf(level);
}

export function resolvePageDefinition(pageId) {
  return PAGE_DEFINITIONS.find((page) => page.id === pageId);
}

export function listPages() {
  return PAGE_DEFINITIONS.slice();
}

export function cloneMatrix(matrix) {
  return JSON.parse(JSON.stringify(matrix || {}));
}

export function cloneLimitedConfig(config) {
  return JSON.parse(JSON.stringify(config || {}));
}
